import React, { useState, useMemo, useEffect } from "react";

export default function HoleStatsBreakdown({
  holeStats = [],
  activeName,
  authId,
  onBack,
  courses = [],
  derivedHomeId,
  defaultMode = "hist",
  scores = {},
  playerKey = null,
  roundHistory = [],
}) {
  const [overviewMode, setOverviewMode] = useState(defaultMode);
  const [activeTab, setActiveTab] = useState("overview");
  const [detailMode, setDetailMode] = useState("hist");
  // Update overviewMode when defaultMode changes
  useEffect(() => {
    setOverviewMode(defaultMode);
  }, [defaultMode]);

  // --- FILTER STATS FOR THIS PLAYER ---
  const myStats = useMemo(
    () => holeStats.filter((s) =>
      s.player_name === activeName ||
      s.player_name === authId ||
      s.user_id === activeName ||
      s.user_id === authId
    ),
    [holeStats, activeName, authId]
  );
  console.log("activeName:", activeName);
  console.log("all holeStats:", JSON.stringify(holeStats));
  console.log("myStats:", JSON.stringify(myStats));
  console.log("activeName:", activeName);
  console.log("first holeStats player_name:", holeStats[0]?.player_name);
  console.log("holeStats length:", holeStats.length);
  const liveStats = useMemo(
    () => myStats.filter((s) => s.is_live === true),
    [myStats]
  );

  const historicalStats = useMemo(
    () => myStats.filter((s) => !s.is_live),
    [myStats]
  );

  const lastRoundStats = useMemo(() => {
    if (myStats.length === 0) return { roundId: null, date: null, dateLabel: null, holes: [] };
    const sorted = [...myStats].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    const lastRoundId = sorted[0]?.round_id;
    const holes = myStats
      .filter((s) => s.round_id === lastRoundId)
      .sort((a, b) => a.hole_number - b.hole_number);

    // Only use roundHistory for the date — never created_at (timezone offset issues)
    let dateLabel = null;
    if (lastRoundId && roundHistory?.length > 0) {
      const historyRound = roundHistory.find((r) => r.id === lastRoundId);
      if (historyRound?.date) {
        dateLabel = new Date(historyRound.date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
    }

    return { roundId: lastRoundId, dateLabel, holes };
  }, [myStats, roundHistory]);

  console.log("activeName (UUID):", activeName);
  console.log("holeStats length:", holeStats.length);
  console.log("first holeStats player_name:", holeStats[0]?.player_name);
  console.log("myStats length:", myStats.length);
  // --- HELPER: Calculate stats from a set of hole stats ---
  const calcStats = (statSet) => {
    let fwAttempts = 0,
      fwHit = 0;
    let girAttempts = 0,
      girHit = 0;
    let totalPutts = 0,
      puttHoles = 0;
    let udAttempts = 0,
      udSuccess = 0;
    let onePutts = 0,
      twoPutts = 0,
      threePlusPutts = 0;
    let girBirdies = 0,
      girHoles = 0;

    statSet.forEach((s) => {
      if (
        s.fairway_hit !== null &&
        s.fairway_hit !== undefined &&
        s.fairway_hit !== ""
      ) {
        const isPenalty = s.fairway_hit?.includes("+Penalty");
        const baseHit = s.fairway_hit?.replace("+Penalty", "");
        fwAttempts++;
        if (baseHit === "Hit") fwHit++;
        // Penalty holes are counted as misses (no fwHit increment)
      }

      // Fix GIR — handle string "false" and "true"
      if (
        s.green_in_regulation !== null &&
        s.green_in_regulation !== undefined &&
        s.green_in_regulation !== "" &&
        s.green_in_regulation !== "Penalty"
      ) {
        girAttempts++;
        const girMade =
          s.green_in_regulation === "Hit" ||
          s.green_in_regulation === true ||
          s.green_in_regulation === "true"; // 👈 ADD THIS
        if (girMade) {
          girHit++;
          girHoles++;
          const girScore = s.score || scores?.[playerKey]?.[s.hole_number];
          if (girScore && s.par && Number(girScore) <= Number(s.par) - 1)
            girBirdies++;
        } else if (
          s.green_in_regulation === "Left" ||
          s.green_in_regulation === "Right" ||
          s.green_in_regulation === "Short" ||
          s.green_in_regulation === "Long"
        ) {
          udAttempts++;
          const holeScore =
            s.score ||
            scores?.[activeName]?.[s.hole_number] ||
            scores?.[playerKey]?.[s.hole_number];
          const holePar = s.par;
          if (
            holeScore !== null &&
            holeScore !== undefined &&
            Number(holeScore) > 0 &&
            holePar !== null &&
            holePar !== undefined &&
            Number(holeScore) <= Number(holePar)
          )
            udSuccess++;
        }
      }

      // Fix putts — only count if putts > 0
      if (s.putts !== null && s.putts !== undefined && Number(s.putts) > 0) {
        const p = Number(s.putts);
        totalPutts += p;
        puttHoles++;
        if (p === 1) onePutts++;
        else if (p === 2) twoPutts++;
        else if (p >= 3) threePlusPutts++;
      }
    });

    return {
      fwPct: fwAttempts > 0 ? Math.round((fwHit / fwAttempts) * 100) : null,
      fwHit,
      fwAttempts,
      girPct: girAttempts > 0 ? Math.round((girHit / girAttempts) * 100) : null,
      girHit,
      girAttempts,
      avgPuttsRound:
        puttHoles > 0 ? ((totalPutts / puttHoles) * 18).toFixed(1) : null,
      avgPuttsHole: puttHoles > 0 ? (totalPutts / puttHoles).toFixed(2) : null,
      totalPutts,
      puttHoles,
      onePuttPct:
        puttHoles > 0 ? Math.round((onePutts / puttHoles) * 100) : null,
      twoPuttPct:
        puttHoles > 0 ? Math.round((twoPutts / puttHoles) * 100) : null,
      threePlusPuttPct:
        puttHoles > 0 ? Math.round((threePlusPutts / puttHoles) * 100) : null,
      udPct: udAttempts > 0 ? Math.round((udSuccess / udAttempts) * 100) : null,
      udSuccess,
      udAttempts,
      girBirdiePct:
        girHoles > 0 ? Math.round((girBirdies / girHoles) * 100) : null,
    };
  };

  const totalStats = useMemo(() => calcStats(myStats), [myStats]);
  const liveStatsCalc = useMemo(() => calcStats(liveStats), [liveStats]);
  const histStats = useMemo(() => calcStats(historicalStats), [historicalStats]);
  const lastRoundCalcStats = useMemo(() => calcStats(lastRoundStats.holes), [lastRoundStats]);

  const lastRoundScore = lastRoundStats.holes.length > 0
    ? lastRoundStats.holes.reduce((sum, s) => sum + (Number(s.score) || 0), 0) || null
    : null;

  // --- PER HOLE STATS HELPER ---
  const computePerHoleStats = (statSet) => {
    const holes = {};
    for (let i = 1; i <= 18; i++) {
      holes[i] = { fwAttempts: 0, fwHit: 0, girAttempts: 0, girHit: 0, totalPutts: 0, puttCount: 0 };
    }
    statSet.forEach((s) => {
      const h = s.hole_number;
      if (!holes[h]) return;
      if (s.fairway_hit !== null && s.fairway_hit !== "") {
        holes[h].fwAttempts++;
        const baseHit = s.fairway_hit?.replace("+Penalty", "");
        if (baseHit === "Hit" || s.fairway_hit === true) holes[h].fwHit++;
      }
      if (s.green_in_regulation !== null) {
        holes[h].girAttempts++;
        if (s.green_in_regulation === "Hit" || s.green_in_regulation === true)
          holes[h].girHit++;
      }
      if (s.putts !== null) {
        holes[h].totalPutts += Number(s.putts);
        holes[h].puttCount++;
      }
    });
    return holes;
  };

  const perHoleStats = useMemo(() => computePerHoleStats(historicalStats), [historicalStats]);
  const perHoleStatsLive = useMemo(() => computePerHoleStats(liveStats), [liveStats]);
  const perHoleStatsLastRound = useMemo(() => computePerHoleStats(lastRoundStats.holes), [lastRoundStats]);

  // --- STYLES ---
  const cardStyle = {
    background: "#fff",
    borderRadius: "14px",
    padding: "16px",
    marginBottom: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  };

  const statRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
  };

  const labelStyle = {
    fontSize: "0.7rem",
    color: "#64748b",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const tabStyle = (active) => ({
    flex: 1,
    padding: "8px 4px",
    border: "none",
    borderRadius: "8px",
    background: active ? "#1b4332" : "transparent",
    color: active ? "white" : "#64748b",
    fontWeight: "700",
    fontSize: "0.65rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  const BigStat = ({ label, value, sub, color = "#1b4332" }) => (
    <div style={{ textAlign: "center", flex: 1 }}>
      <div style={{ fontSize: "1.6rem", fontWeight: "900", color }}>
        {value ?? "—"}
      </div>
      <div style={labelStyle}>{label}</div>
      {sub && (
        <div style={{ fontSize: "0.6rem", color: "#94a3b8", marginTop: "2px" }}>
          {sub}
        </div>
      )}
    </div>
  );

  const OverviewRow = ({ label, live, hist, onClick }) => (
    <div
      onClick={onClick}
      style={{
        ...statRowStyle,
        cursor: onClick ? "pointer" : "default",
        padding: "12px 0",
      }}
    >
      <div
        style={{
          width: "80px",
          fontWeight: "700",
          fontSize: "0.85rem",
          color: "#1e293b",
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          textAlign: "center",
          fontWeight: "800",
          fontSize: "1rem",
          color: "#ef4444",
        }}
      >
        {live ?? "—"}
      </div>
      <div
        style={{
          flex: 1,
          textAlign: "center",
          fontWeight: "700",
          fontSize: "0.9rem",
          color: "#64748b",
        }}
      >
        {hist ?? "—"}
      </div>
      {onClick && <div style={{ color: "#94a3b8", fontSize: "0.8rem" }}>›</div>}
    </div>
  );

  const BarRow = ({ label, value, total, color, maxVal }) => {
    const pct = maxVal ? Math.min((value / maxVal) * 100, 100) : value;
    return (
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
      >
        <div
          style={{
            width: "55px",
            fontSize: "0.75rem",
            fontWeight: "600",
            color: "#475569",
          }}
        >
          {label}
        </div>
        <div
          style={{
            flex: 1,
            background: "#f1f5f9",
            borderRadius: "4px",
            height: "8px",
            overflow: "hidden",
            margin: "0 10px",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              background: color,
              height: "100%",
              borderRadius: "4px",
              transition: "width 0.5s ease",
            }}
          />
        </div>
        <div
          style={{
            width: "60px",
            textAlign: "right",
            fontSize: "0.8rem",
            fontWeight: "700",
            color,
          }}
        >
          {total}
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    console.log("lastRoundId:", lastRoundStats.roundId);
    console.log("lastRoundStats holes:", lastRoundStats.holes.length);
    console.log("overviewMode:", overviewMode);

    // Use the same calcStats function for all modes — identical logic for FW, GIR, putts, U/D
    const lastRoundCalc = lastRoundCalcStats;

    // Comparison variables
    const comparisonLabel = overviewMode === "lastRound" ? "LAST ROUND VS HISTORICAL" : "THIS ROUND VS HISTORICAL";
    const comparisonColumnLabel = overviewMode === "lastRound" ? "LAST ROUND" : "THIS ROUND";
    const comparisonFwPct = overviewMode === "lastRound" ? lastRoundCalc.fwPct : liveStatsCalc.fwPct;
    const comparisonGirPct = overviewMode === "lastRound" ? lastRoundCalc.girPct : liveStatsCalc.girPct;
    const comparisonPutts = overviewMode === "lastRound" ? lastRoundCalc.totalPutts : liveStatsCalc.totalPutts;
    const comparisonUdPct = overviewMode === "lastRound" ? lastRoundCalc.udPct : liveStatsCalc.udPct;

    const modeStats =
      overviewMode === "live" ? liveStatsCalc
      : overviewMode === "lastRound" ? lastRoundCalc
      : histStats;

    return (
      <>
        {/* Mode Toggle */}
        <div style={{ display: "flex", background: "#e2e8f0", borderRadius: "10px", padding: "4px", marginBottom: "14px" }}>
          {[
            { key: "hist", label: "Historical" },
            { key: "lastRound", label: `Last Round${lastRoundStats.dateLabel ? ` (${lastRoundStats.dateLabel}${lastRoundScore ? ` - ${lastRoundScore}` : ""})` : ""}` },
            { key: "live", label: "This Round" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setOverviewMode(key)}
              style={{
                flex: 1,
                padding: "8px",
                border: "none",
                borderRadius: "8px",
                background: overviewMode === key ? "#1b4332" : "transparent",
                color: overviewMode === key ? "white" : "#64748b",
                fontWeight: "700",
                fontSize: "0.65rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          {[
            {
              label: "Fairway %",
              value: modeStats.fwPct !== null ? `${modeStats.fwPct}%` : "—",
              color: "#1b4332",
              sub: `${modeStats.fwHit}/${modeStats.fwAttempts} hit`,
            },
            {
              label: "GIR %",
              value: modeStats.girPct !== null ? `${modeStats.girPct}%` : "—",
              color: "#10b981",
              sub: `${modeStats.girHit}/${modeStats.girAttempts} hit`,
            },
            {
              label: overviewMode === "live" ? "Putts/Hole" : "Putts/Round",
              value: overviewMode === "live"
                ? (modeStats.avgPuttsHole ?? "—")
                : (modeStats.totalPutts > 0 ? modeStats.totalPutts : (modeStats.avgPuttsRound ?? "—")),
              color: "#3b82f6",
              sub: overviewMode === "live"
                ? `${modeStats.puttHoles} holes tracked`
                : `${modeStats.avgPuttsHole ?? "—"} per hole`,
            },
            {
              label: "Up & Down %",
              value: modeStats.udPct !== null ? `${modeStats.udPct}%` : "—",
              color: "#f59e0b",
              sub: `${modeStats.udSuccess}/${modeStats.udAttempts} att.`,
            },
          ].map(({ label, value, color, sub }) => (
            <div
              key={label}
              style={{ ...cardStyle, marginBottom: 0, textAlign: "center" }}
            >
              <div style={{ fontSize: "1.5rem", fontWeight: "900", color }}>
                {value}
              </div>
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  color: "#1e293b",
                  marginTop: "4px",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontSize: "0.6rem",
                  color: "#94a3b8",
                  marginTop: "2px",
                }}
              >
                {sub}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Table - hidden in Historical mode */}
        {overviewMode !== "hist" && (
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: "800",
              color: "#1b4332",
              marginBottom: "12px",
              textTransform: "uppercase",
            }}
          >
            {comparisonLabel}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <div style={{ ...labelStyle, width: "80px" }}></div>
            <div
              style={{
                ...labelStyle,
                flex: 1,
                textAlign: "center",
                color: "#ef4444",
              }}
            >
              {comparisonColumnLabel}
            </div>
            <div style={{ ...labelStyle, flex: 1, textAlign: "center" }}>
              HISTORICAL
            </div>
            <div style={{ width: "12px" }}></div>
          </div>
          <OverviewRow
            label="FW %"
            live={comparisonFwPct !== null && comparisonFwPct !== undefined ? `${comparisonFwPct}%` : "—"}
            hist={histStats.fwPct !== null ? `${histStats.fwPct}%` : "—"}
            onClick={() => setActiveTab("fairway")}
          />
          <OverviewRow
            label="GIR %"
            live={comparisonGirPct !== null && comparisonGirPct !== undefined ? `${comparisonGirPct}%` : "—"}
            hist={histStats.girPct !== null ? `${histStats.girPct}%` : "—"}
            onClick={() => setActiveTab("gir")}
          />
          <OverviewRow
            label="Putts"
            live={comparisonPutts > 0 ? comparisonPutts : "—"}
            hist={histStats.avgPuttsRound ?? "—"}
            onClick={() => setActiveTab("putts")}
          />
          <OverviewRow
            label="U/D %"
            live={comparisonUdPct !== null && comparisonUdPct !== undefined ? `${comparisonUdPct}%` : "—"}
            hist={histStats.udPct !== null ? `${histStats.udPct}%` : "—"}
            onClick={() => setActiveTab("updown")}
          />
        </div>
        )}

        {/* Putt Distribution */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: "800",
              color: "#1b4332",
              marginBottom: "12px",
              textTransform: "uppercase",
            }}
          >
            Putt Distribution
          </div>
          <BarRow
            label="1-Putt"
            value={modeStats.onePuttPct ?? 0}
            total={`${modeStats.onePuttPct ?? 0}%`}
            color="#10b981"
            maxVal={100}
          />
          <BarRow
            label="2-Putt"
            value={modeStats.twoPuttPct ?? 0}
            total={`${modeStats.twoPuttPct ?? 0}%`}
            color="#3b82f6"
            maxVal={100}
          />
          <BarRow
            label="3+ Putt"
            value={modeStats.threePlusPuttPct ?? 0}
            total={`${modeStats.threePlusPuttPct ?? 0}%`}
            color="#ef4444"
            maxVal={100}
          />
        </div>

        {/* Birdie Conversion */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: "800",
              color: "#1b4332",
              marginBottom: "12px",
              textTransform: "uppercase",
            }}
          >
            Birdie Conversion
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{ fontSize: "2rem", fontWeight: "900", color: "#10b981" }}
            >
              {modeStats.girBirdiePct !== null
                ? `${modeStats.girBirdiePct}%`
                : "—"}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
              When you hit the green, this is how often you make birdie or
              better
            </div>
          </div>
        </div>
      </>
    );
  };

  // --- SHARED DETAIL MODE TOGGLE ---
  const renderDetailToggle = () => (
    <div style={{ display: "flex", background: "#e2e8f0", borderRadius: "10px", padding: "4px", marginBottom: "14px" }}>
      {[
        { key: "hist", label: "Historical" },
        { key: "lastRound", label: `Last Round${lastRoundStats.dateLabel ? ` (${lastRoundStats.dateLabel}${lastRoundScore ? ` - ${lastRoundScore}` : ""})` : ""}` },
        { key: "live", label: "This Round" },
      ].map(({ key, label }) => (
        <button
          key={key}
          onClick={() => setDetailMode(key)}
          style={{
            flex: 1, padding: "8px", border: "none", borderRadius: "8px",
            background: detailMode === key ? "#1b4332" : "transparent",
            color: detailMode === key ? "white" : "#64748b",
            fontWeight: "700", fontSize: "0.65rem", cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // --- FAIRWAY TAB ---
  const renderFairway = () => {
    const dStats = detailMode === "live" ? liveStatsCalc : detailMode === "lastRound" ? lastRoundCalcStats : histStats;
    const dPerHole = detailMode === "live" ? perHoleStatsLive : detailMode === "lastRound" ? perHoleStatsLastRound : perHoleStats;
    return (
      <>
        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "14px", textTransform: "uppercase" }}>
            Fairway Hit Summary
          </div>
          {renderDetailToggle()}
          <div style={{ display: "flex", gap: "10px" }}>
            <BigStat
              label="FW %"
              value={dStats.fwPct !== null ? `${dStats.fwPct}%` : "—"}
              sub={`${dStats.fwHit}/${dStats.fwAttempts} hit`}
              color="#1b4332"
            />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "12px", textTransform: "uppercase" }}>
            Fairway % by Hole
          </div>
          {Object.entries(dPerHole).map(([hNum, data]) => {
            const pct = data.fwAttempts > 0 ? Math.round((data.fwHit / data.fwAttempts) * 100) : null;
            const hasPenalty = data.penaltyCount > 0;
            return (
              <div key={hNum} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "700", width: "45px", fontSize: "0.8rem" }}>Hole {hNum}</span>
                <div style={{ flex: 1, margin: "0 10px", background: "#f1f5f9", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                  <div style={{ width: pct !== null ? `${pct}%` : "0%", background: "#1b4332", height: "100%", borderRadius: "4px" }} />
                </div>
                <span style={{ fontWeight: "700", color: "#1b4332", width: "40px", textAlign: "right", fontSize: "0.85rem" }}>
                  {pct !== null ? `${pct}%` : "—"}
                </span>
                {hasPenalty && <span style={{ marginLeft: "6px", fontSize: "0.65rem", color: "#ef4444", fontWeight: "800" }}>P</span>}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // --- GIR TAB ---
  const renderGIR = () => {
    const dStats = detailMode === "live" ? liveStatsCalc : detailMode === "lastRound" ? lastRoundCalcStats : histStats;
    const dPerHole = detailMode === "live" ? perHoleStatsLive : detailMode === "lastRound" ? perHoleStatsLastRound : perHoleStats;
    return (
      <>
        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "14px", textTransform: "uppercase" }}>
            Greens in Regulation Summary
          </div>
          {renderDetailToggle()}
          <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
            <BigStat
              label="GIR %"
              value={dStats.girPct !== null ? `${dStats.girPct}%` : "—"}
              sub={`${dStats.girHit}/${dStats.girAttempts} hit`}
              color="#10b981"
            />
            <BigStat
              label="Birdie Conv."
              value={dStats.girBirdiePct !== null ? `${dStats.girBirdiePct}%` : "—"}
              color="#10b981"
            />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "12px", textTransform: "uppercase" }}>
            GIR % by Hole
          </div>
          {Object.entries(dPerHole).map(([hNum, data]) => {
            const pct = data.girAttempts > 0 ? Math.round((data.girHit / data.girAttempts) * 100) : null;
            return (
              <div key={hNum} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "700", width: "45px", fontSize: "0.8rem" }}>Hole {hNum}</span>
                <div style={{ flex: 1, margin: "0 10px", background: "#f1f5f9", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                  <div style={{ width: pct !== null ? `${pct}%` : "0%", background: "#10b981", height: "100%", borderRadius: "4px" }} />
                </div>
                <span style={{ fontWeight: "700", color: "#10b981", width: "40px", textAlign: "right", fontSize: "0.85rem" }}>
                  {pct !== null ? `${pct}%` : "—"}
                </span>
                <span style={{ color: "#94a3b8", fontSize: "0.65rem", width: "35px", textAlign: "right" }}>
                  {data.girAttempts > 0 ? `${data.girHit}/${data.girAttempts}` : ""}
                </span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // --- PUTTS TAB ---
  const renderPutts = () => {
    const dStats = detailMode === "live" ? liveStatsCalc : detailMode === "lastRound" ? lastRoundCalcStats : histStats;
    const dPerHole = detailMode === "live" ? perHoleStatsLive : detailMode === "lastRound" ? perHoleStatsLastRound : perHoleStats;
    const isLive = detailMode === "live";
    return (
      <>
        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "14px", textTransform: "uppercase" }}>
            Putting Summary
          </div>
          {renderDetailToggle()}
          <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
            <BigStat
              label={isLive ? "Total Putts" : "Putts/Round"}
              value={isLive ? (dStats.totalPutts > 0 ? dStats.totalPutts : "—") : (dStats.avgPuttsRound ?? "—")}
              sub={isLive ? "this round" : `${dStats.avgPuttsHole ?? "—"} per hole`}
              color="#3b82f6"
            />
            <BigStat
              label="1-Putt %"
              value={dStats.onePuttPct !== null ? `${dStats.onePuttPct}%` : "—"}
              color="#10b981"
            />
          </div>
          <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "12px" }}>
            <div style={{ ...labelStyle, marginBottom: "10px" }}>Putt Distribution</div>
            <BarRow label="1-Putt" value={dStats.onePuttPct ?? 0} total={`${dStats.onePuttPct ?? 0}%`} color="#10b981" maxVal={100} />
            <BarRow label="2-Putt" value={dStats.twoPuttPct ?? 0} total={`${dStats.twoPuttPct ?? 0}%`} color="#3b82f6" maxVal={100} />
            <BarRow label="3+ Putt" value={dStats.threePlusPuttPct ?? 0} total={`${dStats.threePlusPuttPct ?? 0}%`} color="#ef4444" maxVal={100} />
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "12px", textTransform: "uppercase" }}>
            Avg Putts by Hole
          </div>
          {Object.entries(dPerHole).map(([hNum, data]) => {
            const avg = data.puttCount > 0 ? (data.totalPutts / data.puttCount).toFixed(1) : null;
            const barPct = avg !== null ? Math.min((Number(avg) / 4) * 100, 100) : 0;
            const color = avg === null ? "#ccc" : Number(avg) <= 1.5 ? "#10b981" : Number(avg) <= 2 ? "#3b82f6" : "#ef4444";
            return (
              <div key={hNum} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "700", width: "45px", fontSize: "0.8rem" }}>Hole {hNum}</span>
                <div style={{ flex: 1, margin: "0 10px", background: "#f1f5f9", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                  <div style={{ width: `${barPct}%`, background: color, height: "100%", borderRadius: "4px" }} />
                </div>
                <span style={{ fontWeight: "700", color, width: "40px", textAlign: "right", fontSize: "0.85rem" }}>{avg ?? "—"}</span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // --- LAST ROUND TAB ---
  const renderLastRound = () => {
    if (!lastRoundStats.roundId || lastRoundStats.holes.length === 0) {
      return (
        <div style={{ ...cardStyle, textAlign: "center", color: "#94a3b8", padding: "32px" }}>
          No detailed stats recorded for your most recent round
        </div>
      );
    }

    const scoreColor = (score, par) => {
      if (!score || !par) return "#1e293b";
      const diff = Number(score) - Number(par);
      if (diff <= -2) return "#f59e0b";
      if (diff === -1) return "#10b981";
      if (diff === 0) return "#3b82f6";
      if (diff === 1) return "#f97316";
      return "#ef4444";
    };

    const colGrid = "32px 28px 32px 40px 40px 40px 28px";

    return (
      <div style={cardStyle}>
        <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "14px", textTransform: "uppercase" }}>
          Round Date: {lastRoundStats.dateLabel || "—"}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: colGrid, gap: "4px", marginBottom: "8px", paddingBottom: "8px", borderBottom: "2px solid #e2e8f0" }}>
          {["Hole", "Par", "Score", "FW", "GIR", "Putts", "U/D"].map((h) => (
            <div key={h} style={{ fontSize: "0.6rem", fontWeight: "700", color: "#64748b", textAlign: "center", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>

        {lastRoundStats.holes.map((s) => {
          const fw = s.fairway_hit;
          const fwDisplay = fw == null || fw === ""
            ? "—"
            : fw === "Hit" ? "✓"
            : fw.includes("+Penalty") ? fw.replace("+Penalty", "") + "⚠"
            : fw;
          const fwColor = fwDisplay === "✓" ? "#10b981" : "#94a3b8";

          const gir = s.green_in_regulation;
          const girHit = gir === "Hit" || gir === true || gir === "true";
          const girDisplay = gir == null || gir === ""
            ? "—"
            : girHit ? "✓"
            : gir === "Left" ? "L"
            : gir === "Right" ? "R"
            : gir === "Short" ? "Sh"
            : gir === "Long" ? "Lg"
            : "—";
          const girColor = girHit ? "#10b981" : "#94a3b8";

          const missedGir = gir && !girHit && gir !== "Penalty";
          const udMade = missedGir && s.score && s.par && Number(s.score) <= Number(s.par);
          const udDisplay = !missedGir ? "—" : udMade ? "✓" : "✗";
          const udColor = udDisplay === "✓" ? "#10b981" : udDisplay === "✗" ? "#ef4444" : "#94a3b8";

          return (
            <div key={s.hole_number} style={{ display: "grid", gridTemplateColumns: colGrid, gap: "4px", padding: "7px 0", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
              <div style={{ fontSize: "0.8rem", fontWeight: "700", textAlign: "center" }}>{s.hole_number}</div>
              <div style={{ fontSize: "0.8rem", textAlign: "center", color: "#64748b" }}>{s.par ?? "—"}</div>
              <div style={{ fontSize: "0.85rem", fontWeight: "800", textAlign: "center", color: scoreColor(s.score, s.par) }}>{s.score ?? "—"}</div>
              <div style={{ fontSize: "0.75rem", textAlign: "center", color: fwColor }}>{fwDisplay}</div>
              <div style={{ fontSize: "0.75rem", textAlign: "center", color: girColor }}>{girDisplay}</div>
              <div style={{ fontSize: "0.8rem", fontWeight: "600", textAlign: "center", color: "#3b82f6" }}>{s.putts ?? "—"}</div>
              <div style={{ fontSize: "0.75rem", textAlign: "center", color: udColor }}>{udDisplay}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // --- UP/DOWN TAB ---
  const renderUpDown = () => {
    const dStats = detailMode === "live" ? liveStatsCalc : detailMode === "lastRound" ? lastRoundCalcStats : histStats;
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#1b4332", marginBottom: "14px", textTransform: "uppercase" }}>
          Up and Down Summary
        </div>
        {renderDetailToggle()}
        <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
          <BigStat
            label="U/D %"
            value={dStats.udPct !== null ? `${dStats.udPct}%` : "—"}
            sub={`${dStats.udSuccess}/${dStats.udAttempts} att.`}
            color="#f59e0b"
          />
        </div>
        <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "12px" }}>
          <div style={labelStyle}>Scrambling %</div>
          <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginBottom: "8px", marginTop: "4px" }}>
            Par or better after missing the GIR
          </div>
          <div style={{ fontSize: "1.8rem", fontWeight: "900", color: dStats.udPct !== null ? "#f59e0b" : "#94a3b8" }}>
            {dStats.udPct !== null ? `${dStats.udPct}%` : "—"}
          </div>
        </div>
      </div>
    );
  };
  return (
    <div
      style={{
        backgroundColor: "#f4f4f5",
        minHeight: "100vh",
        paddingBottom: "40px",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          padding: "15px 20px 15px 65px", // 👈 65px left padding clears the hamburger
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e2e8f0",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <h2 style={{ margin: 0, color: "#1b4332", fontSize: "1.2rem" }}>
          Detailed Hole Stats
        </h2>
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            color: "#ccc",
            cursor: "pointer",
          }}
        >
          x
        </button>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          padding: "10px 15px 10px 65px",
          background: "#f1f5f9",
          position: "sticky",
          top: "57px",
          zIndex: 9,
        }}
      >
        {[
          { key: "overview", label: "Overview" },
          { key: "fairway", label: "FW" },
          { key: "gir", label: "GIR" },
          { key: "putts", label: "Putts" },
          { key: "updown", label: "U/D" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={tabStyle(activeTab === key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "15px" }}>
        {activeTab === "overview" && renderOverview()}
        {activeTab === "fairway" && renderFairway()}
        {activeTab === "gir" && renderGIR()}
        {activeTab === "putts" && renderPutts()}
        {activeTab === "updown" && renderUpDown()}
      </div>
    </div>
  );
}
